/**
 * Accessibility behaviour layered on top of the compiled theme.
 *
 * assets/theme.js is minified and the vendor marks it "do not modify"; the
 * unminified assets/theme.dev.js is 2x the size and is not what ships. So the
 * ARIA *state* that the theme's own components do not maintain is maintained
 * here instead, keyed off the classes those components already toggle.
 *
 * Markup-side ARIA (roles, ids, labels) lives in the Liquid. This file only
 * keeps state in sync and adds the keyboard handling that was missing.
 *
 * Loaded from snippets/head.liquid after theme.js.
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------------
   * 1. Modal drawers — aria-modal in step with the open state
   *
   * These elements stay in the DOM while closed, so a static
   * aria-modal="true" would tell assistive tech to ignore the rest of the
   * page even with the drawer shut. The theme toggles `is-open`; mirror it.
   * ---------------------------------------------------------------- */

  var MODAL_SELECTOR = 'cart-element[role="dialog"], drawer-element[role="dialog"]';

  function syncModal(el) {
    if (el.classList.contains('is-open')) {
      el.setAttribute('aria-modal', 'true');
    } else {
      el.removeAttribute('aria-modal');
    }
  }

  function watchModals() {
    document.querySelectorAll(MODAL_SELECTOR).forEach(function (el) {
      if (el.dataset.a11yModalBound) return;
      el.dataset.a11yModalBound = 'true';
      syncModal(el);
      new MutationObserver(function () { syncModal(el); })
        .observe(el, { attributes: true, attributeFilter: ['class'] });
    });
  }

  /* ------------------------------------------------------------------
   * 2. Tabs — aria-selected and a roving tabindex
   *
   * theme.js already handles click and Enter/Space on [data-tabs-link] and
   * marks the active one with `.current`. It never updates ARIA state, so
   * the selected tab announced as an unnamed list item with no state.
   * ---------------------------------------------------------------- */

  function syncTablist(list) {
    var tabs = list.querySelectorAll('[role="tab"]');
    tabs.forEach(function (tab) {
      var selected = tab.classList.contains('current');
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      // Roving tabindex: only the selected tab is a tab stop.
      tab.setAttribute('tabindex', selected ? '0' : '-1');
    });
    // Nothing marked current (initial render): make the first tab reachable.
    if (!list.querySelector('[role="tab"].current') && tabs.length) {
      tabs[0].setAttribute('tabindex', '0');
    }
  }

  function watchTablists() {
    document.querySelectorAll('[role="tablist"]').forEach(function (list) {
      if (list.dataset.a11yTabsBound) return;
      list.dataset.a11yTabsBound = 'true';
      syncTablist(list);

      new MutationObserver(function () { syncTablist(list); })
        .observe(list, { attributes: true, subtree: true, attributeFilter: ['class'] });

      // Arrow-key navigation between tabs, per the ARIA tabs pattern.
      list.addEventListener('keydown', function (event) {
        var keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
        if (keys.indexOf(event.key) === -1) return;

        var tabs = Array.prototype.slice.call(list.querySelectorAll('[role="tab"]'));
        if (tabs.length < 2) return;

        var current = document.activeElement;
        var i = tabs.indexOf(current);
        if (i === -1) return;

        event.preventDefault();
        var next;
        if (event.key === 'Home') next = tabs[0];
        else if (event.key === 'End') next = tabs[tabs.length - 1];
        else if (event.key === 'ArrowLeft') next = tabs[(i - 1 + tabs.length) % tabs.length];
        else next = tabs[(i + 1) % tabs.length];

        next.setAttribute('tabindex', '0');
        next.focus();
        next.click(); // theme.js listens for click to switch panels
      });
    });
  }

  /* ------------------------------------------------------------------
   * 3. Listbox popouts — keep aria-selected honest
   *
   * The theme marks the chosen option with `.select-popout__item--current`
   * but never updates aria-selected, so screen readers kept announcing the
   * originally-rendered option as selected.
   * ---------------------------------------------------------------- */

  function syncListbox(list) {
    list.querySelectorAll('[role="option"]').forEach(function (opt) {
      var selected = opt.classList.contains('select-popout__item--current');
      opt.setAttribute('aria-selected', selected ? 'true' : 'false');
    });
  }

  function watchListboxes() {
    document.querySelectorAll('[role="listbox"]').forEach(function (list) {
      if (list.dataset.a11yListboxBound) return;
      list.dataset.a11yListboxBound = 'true';
      new MutationObserver(function () { syncListbox(list); })
        .observe(list, { attributes: true, subtree: true, attributeFilter: ['class'] });
    });
  }

  /* ------------------------------------------------------------------
   * 4. Price range slider — keyboard operation
   *
   * range-slider.js is pointer-only: it binds mousedown/touchstart and
   * writes data-se-min-value / data-se-max-value. The handles now carry
   * role="slider", so they also need arrow keys and live value updates.
   * ---------------------------------------------------------------- */

  function initRangeSlider(slider) {
    if (slider.dataset.a11yRangeBound) return;
    slider.dataset.a11yRangeBound = 'true';

    var left = slider.querySelector('[data-range-left]');
    var right = slider.querySelector('[data-range-right]');
    var holder = slider.closest('[data-range-holder]');
    if (!left || !right || !holder) return;

    var minInput = holder.querySelector('[data-field-price-min]');
    var maxInput = holder.querySelector('[data-field-price-max]');
    if (!minInput || !maxInput) return;

    var absMin = parseInt(slider.getAttribute('data-se-min'), 10) || 0;
    var absMax = parseInt(slider.getAttribute('data-se-max'), 10) || 0;
    if (!absMax) return;

    // One percent of the range per press, but never less than 1 unit.
    var step = Math.max(1, Math.round((absMax - absMin) / 100));
    var symbol = (slider.getAttribute('data-currency-symbol') || '').trim();

    function currentValue(handle) {
      var input = handle === left ? minInput : maxInput;
      var raw = input.value !== '' ? input.value : input.placeholder;
      var n = parseInt(raw, 10);
      if (isNaN(n)) n = handle === left ? absMin : absMax;
      return n;
    }

    function apply(handle, value) {
      // Clamp to the track, and never let the handles cross.
      var lo = handle === left ? absMin : currentValue(left);
      var hi = handle === left ? currentValue(right) : absMax;
      value = Math.min(Math.max(value, lo), hi);

      var input = handle === left ? minInput : maxInput;
      input.value = value;
      handle.setAttribute('aria-valuenow', String(value));
      handle.setAttribute('aria-valuetext', symbol + value);

      // range-slider.js and the filter form both listen for change.
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    function onKeydown(event) {
      var handle = event.currentTarget;
      var value = currentValue(handle);
      var key = event.key;
      var next;

      if (key === 'ArrowLeft' || key === 'ArrowDown') next = value - step;
      else if (key === 'ArrowRight' || key === 'ArrowUp') next = value + step;
      else if (key === 'PageDown') next = value - step * 10;
      else if (key === 'PageUp') next = value + step * 10;
      else if (key === 'Home') next = handle === left ? absMin : currentValue(left);
      else if (key === 'End') next = handle === left ? currentValue(right) : absMax;
      else return;

      event.preventDefault();
      apply(handle, next);
    }

    [left, right].forEach(function (handle) {
      handle.addEventListener('keydown', onKeydown);
    });

    // Keep the announced value correct when the handles are dragged instead.
    new MutationObserver(function () {
      left.setAttribute('aria-valuenow', String(currentValue(left)));
      left.setAttribute('aria-valuetext', symbol + currentValue(left));
      right.setAttribute('aria-valuenow', String(currentValue(right)));
      right.setAttribute('aria-valuetext', symbol + currentValue(right));
    }).observe(slider, {
      attributes: true,
      attributeFilter: ['data-se-min-value', 'data-se-max-value']
    });
  }

  function watchRangeSliders() {
    document.querySelectorAll('range-slider[data-range-slider]').forEach(initRangeSlider);
  }


  /* ------------------------------------------------------------------
   * 5. Status messages the theme changes silently
   *
   * assets/share-button.js and the cart component are both minified, so the
   * announcements are wired here off the events and attributes they already
   * expose.
   * ---------------------------------------------------------------- */

  function wireShareStatus() {
    document.querySelectorAll('share-button').forEach(function (host) {
      if (host.dataset.a11yShareBound) return;
      host.dataset.a11yShareBound = 'true';

      var button = host.querySelector('[data-share-button]');
      var status = host.querySelector('[data-share-status]');
      var tooltip = host.querySelector('tooltip-component');
      if (!button || !status) return;

      // The translated "Link copied" string already lives on the tooltip.
      var message = (tooltip && tooltip.getAttribute('data-tooltip')) || '';
      if (!message) return;

      button.addEventListener('click', function () {
        // Only the copy-to-clipboard path shows the tooltip; the native share
        // sheet is its own UI and needs no announcement.
        if (navigator.share) return;
        status.textContent = '';
        // A fresh text node is what triggers the announcement.
        setTimeout(function () { status.textContent = message; }, 50);
        setTimeout(function () { status.textContent = ''; }, 4000);
      });
    });
  }

  function wireCartStatus() {
    var announce = document.querySelector('[data-cart-announce]');
    if (!announce || announce.dataset.a11yCartBound) return;
    announce.dataset.a11yCartBound = 'true';

    function say() {
      var qty = document.querySelector('[data-cart-items-qty]');
      var text = qty ? qty.textContent.trim() : '';
      if (!text) return;
      announce.textContent = '';
      setTimeout(function () { announce.textContent = text; }, 50);
    }

    ['theme:cart:added', 'theme:cart:update'].forEach(function (name) {
      document.addEventListener(name, say);
    });
  }

  /* ---------------------------------------------------------------- */

  function scan() {
    watchModals();
    watchTablists();
    watchListboxes();
    watchRangeSliders();
    wireShareStatus();
    wireCartStatus();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }

  // Drawers, quick view and filter results are injected after load, and the
  // theme editor re-renders sections in place.
  if ('MutationObserver' in window) {
    var rescan;
    new MutationObserver(function () {
      clearTimeout(rescan);
      rescan = setTimeout(scan, 100);
    }).observe(document.body, { childList: true, subtree: true });
  }

  document.addEventListener('shopify:section:load', scan);
})();
